import { Button, TextField } from "@ths/ui";
import type { FC, FormEvent } from "react";
import { useEffect, useRef, useState } from "react";

import type { CheckInContact } from "../components/ReminderConsentFields";
import Spinner from "../components/Spinner";
import type { SelfCheckinLookupReservation } from "../generated/graphql";

import { formatShortDisplayDate } from "./dateFormatting";
import { formatEmailInput, getEmailValidationError } from "./emailFormatting";

interface ConfirmStepProps {
  lookup: SelfCheckinLookupReservation;
  isSubmitting: boolean;
  error: string;
  initialEmail?: string;
  flow?: "checkin" | "checkout" | "request" | "reservation";
  onConfirm: (contact: CheckInContact) => void;
  onBack: () => void;
}

const ConfirmStep: FC<ConfirmStepProps> = ({
  lookup,
  isSubmitting,
  error,
  initialEmail = "",
  flow = "checkin",
  onConfirm,
  onBack,
}) => {
  const { hasEmail } = lookup;
  const [email, setEmail] = useState(() => formatEmailInput(initialEmail));
  const [emailTouched, setEmailTouched] = useState(() =>
    Boolean(initialEmail.trim()),
  );
  const emailContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!hasEmail) emailContainer.current?.querySelector("input")?.focus();
  }, [hasEmail]);

  const emailValidationError = hasEmail ? "" : getEmailValidationError(email);
  const visibleEmailError = emailTouched ? emailValidationError : "";
  const canSubmit = (hasEmail || !emailValidationError) && !isSubmitting;

  const handleSubmit = (e?: FormEvent) => {
    e?.preventDefault();
    setEmailTouched(true);
    if (!canSubmit) return;
    onConfirm({
      email: hasEmail ? null : email,
    });
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="bc-fade-up w-full max-w-sm space-y-6"
    >
      <div className="text-center">
        <h1 className="font-oswald text-4xl font-medium uppercase tracking-wide text-on-navy sm:text-5xl">
          {lookup.guestFirstName} {lookup.guestLastName}
        </h1>
        <div className="mt-5 inline-flex max-w-full items-center gap-2.5">
          <span
            aria-hidden="true"
            className="size-2 shrink-0 rounded-full bg-highlight"
          />
          <p className="min-w-0 font-oswald text-base font-medium uppercase leading-snug tracking-[0.12em] text-on-navy">
            {lookup.basecampName}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-on-navy/[0.06] bg-on-navy/[0.03]">
        <div className="grid grid-cols-2 divide-x divide-on-navy/[0.06]">
          <div className="p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-on-navy/25">
              Arrive
            </p>
            <p className="mt-1.5 font-oswald text-xl font-light text-on-navy/90">
              {formatShortDisplayDate(lookup.checkInDate)}
            </p>
          </div>
          <div className="p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-on-navy/25">
              Depart
            </p>
            <p className="mt-1.5 font-oswald text-xl font-light text-on-navy/90">
              {lookup.checkOutDate
                ? formatShortDisplayDate(lookup.checkOutDate)
                : "Open"}
            </p>
          </div>
        </div>
      </div>

      {!hasEmail && (
        <div
          ref={emailContainer}
          className="[&_label]:text-on-navy/80 [&_p]:text-error-surface"
        >
          <TextField
            label="Your Email"
            type="email"
            autoComplete="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(formatEmailInput(e.target.value))}
            onBlur={() => setEmailTouched(true)}
            placeholder="name@example.com"
            error={visibleEmailError || undefined}
            className="rounded-xl border-on-navy/30 bg-on-navy/5 text-on-navy placeholder:text-on-navy/60 focus:ring-on-navy"
          />
        </div>
      )}

      {error && (
        <div className="bc-fade-up rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-center text-sm font-medium text-red-300">
          {error}
        </div>
      )}

      <Button
        type="submit"
        variant="ghost"
        size="lg"
        disabled={!canSubmit}
        className={`h-auto min-h-12 w-full whitespace-normal break-words rounded-xl px-8 py-4 font-oswald text-lg font-medium uppercase tracking-wider focus-visible:ring-on-navy focus-visible:ring-offset-navy-strong active:scale-[0.98] disabled:opacity-100 ${canSubmit ? "bg-highlight text-primaryDarkBlue shadow-lg shadow-highlight/20 hover:bg-highlight" : "bg-on-navy/[0.06] text-on-navy/50 hover:bg-on-navy/[0.06]"}`}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2">
            <Spinner />
            {flow === "request"
              ? "Preparing request..."
              : flow === "checkout"
                ? "Preparing checkout..."
                : flow === "reservation"
                  ? "Continuing..."
                  : "Checking in..."}
          </span>
        ) : flow === "request" || flow === "checkout" ? (
          "Continue"
        ) : (
          "Yes, That's Me"
        )}
      </Button>

      <Button
        variant="ghost"
        type="button"
        onClick={onBack}
        disabled={isSubmitting}
        className="w-full py-3 text-sm font-medium text-on-navy/80 hover:bg-on-navy/10"
      >
        Go Back
      </Button>
    </form>
  );
};

export default ConfirmStep;
